// src/modules/app-views/welcome/pages/Init/WelcomeCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Progress, Dropdown, Space } from 'antd';
import { SettingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import translateX from 'utils/translateX';
import { THEME_BACKGROUND } from 'constants/ThemeConstant';
import app from 'configs/ConfigIndex';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import { useEffect, useRef, useState } from 'react';
import { getStorageAuthItem } from 'stores/auth';
import accountService from 'modules/app-views/account/service';
import { validateNextStepsClose } from 'modules/app-views/welcome/pages/Init/NextSteps';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { Title, Text, Paragraph } = Typography;
const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// *******************************************************************************
// STYLED COMPONENTS
// *******************************************************************************
const HeroSection = styled(motion.div)`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 16px;
  padding: 48px 32px;
  margin-bottom: 32px;
  color: white;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 16px;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const SettingsIcon = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;

  .ant-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: rotate(90deg);
    }

    .anticon {
      color: white;
      font-size: 16px;
    }
  }
`;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WelcomeCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const quoteRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const userName = storedUser?.name?.split(' ')[0] || 'Usuário';
  const backgroundURL = THEME_BACKGROUND[app.THEME_COLOR];
  const allSteps = app.WELCOME_NEXT_STEPS;
  const stepsCompleted = storedUser?.welcomeNextSteps || [];
  const completedSteps = stepsCompleted.filter(step => allSteps.includes(step));

  // Check if it's the first time user is logging in
  const isFirstUse = getStorageAuthItem('firstUse') === true;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [quote, setQuote] = useState(null);
  const [setupProgress, setSetupProgress] = useState(0);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [showNextStepsSession, setShowNextStepsSession] = useState(false);
  const [showQuickAccessModules, setShowQuickAccessModules] = useState(true);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadQuote().then();
    calculateSetupProgress();
  }, [storedUser?.welcomeNextSteps]);

  useEffect(() => {
    // Mirror the same logic as WelcomeInit: done=true hides, showNextStepsSession=true re-shows.
    // Backend strips falsy values, so false is never persisted — we rely on truthy anchors.
    const isDone = appService.getModulePreferences({
      category: 'tour',
      moduleId: 'welcome',
      key: 'done',
    });
    const showOverride = appService.getModulePreferences({
      category: 'tour',
      moduleId: 'welcome',
      key: 'showNextStepsSession',
    });
    setShowNextStepsSession(!isDone || showOverride === true);
  }, [storedParameters]);

  // Sync quick access modules visibility with store preferences
  useEffect(() => {
    const shouldShow = appService.getModulePreferences({
      category: 'widgets',
      moduleId: 'welcome',
      key: 'showQuickAccessModules',
    });
    // Default to true if not set
    setShowQuickAccessModules(shouldShow ?? true);
  }, [storedParameters]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const loadQuote = async () => {
    if (quoteRef.current) return;

    try {
      quoteRef.current = true;
      const quoteData = await appService.getQuote();
      setQuote(quoteData);
    } catch (error) {
      console.error('Error loading quote:', error);
    }
  };

  const calculateSetupProgress = () => {
    // Total steps defined in the app
    const totalSteps = allSteps?.length || 0;

    if (totalSteps === 0) {
      setSetupProgress(0);
      return;
    }

    // Calculate percentage
    const progress = Math.round((completedSteps.length / totalSteps) * 100);
    setSetupProgress(progress);
  };

  const handleToggleNextSteps = async () => {
    setIsLoadingPreferences(true);

    if (showNextStepsSession && !validateNextStepsClose({ storedUser, appService })) {
      setIsLoadingPreferences(false);
      return;
    }

    await appService.updateModulePreferences({
      category: 'tour',
      data: { showNextStepsSession: !showNextStepsSession },
      moduleId: 'welcome',
    });

    setIsLoadingPreferences(false);
  };

  const handleToggleQuickAccess = async () => {
    setIsLoadingPreferences(true);

    await appService.updateModulePreferences({
      category: 'widgets',
      data: { showQuickAccessModules: !showQuickAccessModules },
      moduleId: 'welcome',
    });

    setIsLoadingPreferences(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const menuItems = [
    {
      key: 'toggle-quick-access',
      icon: showQuickAccessModules ? <EyeInvisibleOutlined /> : <EyeOutlined />,
      label: translateX(
        showQuickAccessModules ? 'hide_quick_access' : 'show_quick_access'
      ),
      onClick: handleToggleQuickAccess,
      disabled: isLoadingPreferences,
    },
    {
      key: 'toggle-next-steps',
      icon: showNextStepsSession ? <EyeInvisibleOutlined /> : <EyeOutlined />,
      label: translateX(
        showNextStepsSession ? 'hide_next_steps_session' : 'show_next_steps_session'
      ),
      onClick: handleToggleNextSteps,
      disabled: isLoadingPreferences,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <HeroSection
      id="tour-welcome-card"
      variants={heroVariants}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ backgroundImage: `url(${backgroundURL})` }}
    >
      {/* Settings Menu */}
      <SettingsIcon id="tour-welcome-settings">
        <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={['click']}>
          <div className="ant-dropdown-trigger">
            <SettingOutlined spin={isLoadingPreferences} />
          </div>
        </Dropdown>
      </SettingsIcon>

      <Title level={1} style={{ color: 'white', marginBottom: 8 }}>
        {translateX(isFirstUse ? 'welcome' : 'welcome_back')}, {userName}! 🎉
      </Title>

      {/* Show progress bar if showNextStepsSession is true, otherwise show quote */}
      {showNextStepsSession ? (
        <>
          <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16, marginBottom: 24 }}>
            {translateX(
              setupProgress >= 100 ? 'system_configuration_done' : 'user_active_start_configuring'
            )}
          </Paragraph>

          <Progress
            percent={setupProgress}
            strokeColor={{
              '0%': '#fff',
              '100%': '#87e8de',
            }}
            railColor="rgba(255, 255, 255, 0.3)"
            style={{ maxWidth: 400, margin: '0 auto' }}
            showInfo={false}
          />
          <Text style={{ color: 'white', display: 'block', marginTop: 8 }}>
            {setupProgress}% {translateX('setup_completed')}
          </Text>
        </>
      ) : (
        quote && (
          <div style={{ marginTop: 16 }}>
            <Paragraph
              style={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: 14,
                fontStyle: 'italic',
                maxWidth: 500,
                margin: '0 auto',
              }}
            >
              "{quote.quote}"
            </Paragraph>
            {quote.author && (
              <Text
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  display: 'block',
                  marginTop: 8,
                  fontSize: 12,
                }}
              >
                — {quote.author}
              </Text>
            )}
          </div>
        )
      )}
    </HeroSection>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WelcomeCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
